import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EllipsisVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../context/ChatContext';
import ChatDropdown from '../ui/ChatDropdown';
import { getHeading } from '../../utils/getHeading';

const ChatHistory = ({ searchTerm, onItemClick }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const {
    getChatHistory,
    activeChatId,
    chats,
    renameChatById,
    setDeleteChatId,
  } = useChat();
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const buttonRefs = useRef({});

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        await getChatHistory();
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
    setEditingChatId(null);
  }, []);

  const handleRename = (id) => {
    const chat = chats.find((c) => c.chat_id === id);
    setEditingChatId(id);
    setMenuOpenId(null);
    setEditingValue(chat?.name || '');
  };

  // handle delete func
  const handleDelete = (id) => {
    setDeleteChatId(id);
  };

  const handleRenameSubmit = async (id) => {
    const trimmed = editingValue.trim();
    setEditingChatId(null);

    if (!trimmed) return;

    try {
      await renameChatById(id, trimmed);
      toast.success('Chat renamed successfully!');
      await getChatHistory();
    } catch (error) {
      console.error('Rename error:', error);
      toast.error('Failed to rename chat.');
    }
  };

  const filteredChats = chats?.filter((chat) =>
    chat.name.toLowerCase().includes(searchTerm?.toLowerCase() || '')
  );

  const sortedChats = [...(filteredChats || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  // Group chats by heading (date)
  const groupedChats = sortedChats?.reduce((acc, chat) => {
    const heading = getHeading(chat.created_at);
    if (!acc[heading]) acc[heading] = [];
    acc[heading].push(chat);
    return acc;
  }, {});

  return (
    <nav
      className="overflow-y-auto h-[calc(100vh-190px)] pb-[10px] nav-scroll"
      ref={containerRef}
    >
      <ul>
        {loading ? (
          <div className="p-3 text-[#7a7c7c]">Loading...</div>
        ) : filteredChats.length > 0 ? (
          Object.entries(groupedChats).map(([heading, chatsInGroup]) => (
            <React.Fragment key={heading}>
              <h3 className="text-base py-3 px-1 text-[#7A7C7C] font-bold">
                {heading}
              </h3>
              {chatsInGroup.map((chat) => (
                <li className="relative group" key={chat.chat_id}>
                  <div
                    className={`p-3 flex gap-3 rounded-md items-center hover:bg-[#eeeeee] cursor-pointer ${
                      chat.chat_id === activeChatId
                        ? 'font-[600] bg-[#eee]'
                        : ''
                    }`}
                  >
                    <div
                      onClick={() => {
                        navigate(`/chat/${chat.chat_id}`, { replace: true });
                        onItemClick();
                      }}
                      className="w-full text-base break-all text-[#7a7c7c] truncate"
                    >
                      {editingChatId === chat.chat_id ? (
                        <input
                          autoFocus
                          className="w-full p-1 text-sm text-gray-700 bg-white border rounded"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => handleRenameSubmit(chat.chat_id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleRenameSubmit(chat.chat_id);
                            }
                          }}
                        />
                      ) : (
                        chat.name
                      )}
                    </div>
                    <button
                      ref={(el) => {
                        if (el) buttonRefs.current[chat.chat_id] = el;
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setMenuOpenId(
                          menuOpenId === chat.chat_id ? null : chat.chat_id
                        );
                      }}
                      className={`ml-2 p-1 hover:bg-gray-300 rounded ${
                        menuOpenId === chat.chat_id
                          ? 'opacity-100'
                          : 'group-hover:opacity-100 opacity-100 lg:opacity-0'
                      } transition-opacity`}
                    >
                      <EllipsisVertical size={18} />
                    </button>
                    {menuOpenId === chat.chat_id && (
                      <ChatDropdown
                        reference={buttonRefs.current[chat.chat_id]}
                        onRename={() => handleRename(chat.chat_id)}
                        onDelete={(e) => {
                          e.preventDefault();
                          handleDelete(chat.chat_id);
                        }}
                      />
                    )}
                  </div>
                </li>
              ))}
            </React.Fragment>
          ))
        ) : (
          <div className="p-3 text-[#7a7c7c]">No result Found</div>
        )}
      </ul>
    </nav>
  );
};

export default ChatHistory;
